import { action, computed, observable } from 'mobx'
import { debounceTime, filter, finalize, switchMap } from 'rxjs'
import type { QuestionListViewModelFactory } from '@/features/jobs'
import type { Question } from '@/features/jobs/questions/questions'
import type { LegendStore } from '@/features/legends/LegendStore'
import { LegendComputeError } from '@/features/legends/legend-compute-error'
import type { LegendVersion } from '@/features/legends/LegendVersion'
import type {
  OrganizerBinding,
  OrganizerBindings,
} from '@/features/legends/legend-engine'
import type { WorkspaceCatalog } from '@/features/legends/editor/legend-part-dependencies'
import { Types } from '@/features/legends/editor/ryno-types'
import type { PrepAndReviewConfigEditorViewModel } from '@/features/legends/options/PrepAndReviewConfigEditorViewModel'
import type { AnyQuestionEditor } from '@/features/legends/questions/question-registry'
import type { ActivationLifecycle } from '@/infra/activation'
import type { ObjectStorage } from '@/infra/object-storage'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction, toStream } from '@/utils/mobx-util'
import { extractMessageFromError } from '@/utils/error-util'
import { withAbortSignal } from '@/utils/rx-util'
import type {
  LegendComputation,
  LegendComputationInput,
} from './legend-computation'
import { PreviewInputGroupViewModel } from './PreviewInputGroupViewModel'
import {
  mapCollectedValueBindingToQuestion,
  mapRoutingContextSourceToQuestion,
  parseCollectedValue,
} from './preview-questions'
import { computePricing, findUnknownInventoryItemIds } from './price-computer'
import { SkillInputsViewModel } from './SkillInputsViewModel'
import { formatPreviewValue, type PreviewValue } from './preview-values'

/**
 * A tab of the preview's outputs.
 */
export type PreviewOutputTab = 'Computations' | 'Variables'

/**
 * The latest computation of the preview.
 */
export type PreviewResult =
  | { type: 'Loading' }
  | {
      type: 'Error'
      message: string
      /**
       * Whether the engine found errors in the legend, which the editor can
       * fix.
       */
      hasLegendErrors: boolean
    }
  | { type: 'Success'; computation: LegendComputation }

/**
 * A variable's computed value, ready to show.
 */
export interface PreviewVariable {
  id: string
  title: string
  /**
   * The variable's ID, when the title is its label.
   */
  caption: string | null
  /**
   * The list's items, or the single value.
   */
  values: PreviewValue[]
  isList: boolean
}

/**
 * A personal organizer field the computation prefills.
 */
export interface PreviewPrefill {
  bindingId: string
  title: string
  value: string
}

/**
 * Dependencies of the preview.
 */
export interface LegendPreviewViewModelOptions {
  legendStore: LegendStore
  createQuestionListViewModel: QuestionListViewModelFactory
  catalog: WorkspaceCatalog
  getLegendVersion: () => LegendVersion | null
  getOrganizerBindings: () => OrganizerBindings | null
  /**
   * Every question of the legend.
   */
  getQuestions: () => AnyQuestionEditor[]
  getPrepAndReviewConfig: () => PrepAndReviewConfigEditorViewModel
  /**
   * Where the sample input is kept.
   */
  getStorage: () => ObjectStorage
  /**
   * Whether the preview shows, which is when it computes.
   */
  isShowing: () => boolean
  /**
   * Shows the editor, to fix the legend's errors.
   */
  showEditor: () => void
}

/**
 * Runs the legend through the engine with sample answers and shows what the
 * job would look like: price, routing matches, deadline, title and more.
 */
export class LegendPreviewViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  @observable.ref private accessor _result: PreviewResult = { type: 'Loading' }

  readonly questionsVm: PreviewInputGroupViewModel
  readonly dataSourcesVm: PreviewInputGroupViewModel
  readonly organizerVm: PreviewInputGroupViewModel
  readonly skillsVm: SkillInputsViewModel

  @observable accessor outputTab: PreviewOutputTab = 'Computations'
  @observable accessor isComputing = false

  /**
   * Computes when previewing starts, when the legend is saved and when the
   * input changes while previewing. A new computation aborts the previous one.
   */
  private readonly _computations$ = toStream(
    () => ({
      isPreviewing: this._options.isShowing(),
      legendVersion: this._options.getLegendVersion(),
      concurrencyVersion: this._options.getLegendVersion()?.concurrencyVersion,
      input: this._input,
    }),
    true,
  ).pipe(
    filter((x) => x.isPreviewing),
    debounceTime(150),
    switchMap(({ legendVersion, input }) => {
      this._setComputing(true)
      return withAbortSignal((signal) =>
        this._compute(legendVersion, input, signal),
      ).pipe(finalize(() => this._setComputing(false)))
    }),
    tapAction((result: PreviewResult) => this._receiveResult(result)),
  )

  private readonly _organizerQuestions$ = toStream(
    () => this._collectedValueQuestions,
    true,
  ).pipe(tapAction((questions: Question[]) => this.organizerVm.sync(questions)))

  constructor(private readonly _options: LegendPreviewViewModelOptions) {
    const { createQuestionListViewModel, getStorage } = _options
    const getLegendId = () => _options.getLegendVersion()?.id ?? null
    this.questionsVm = new PreviewInputGroupViewModel(
      getStorage,
      getLegendId,
      'previewer.intakeResponses',
      createQuestionListViewModel,
    )
    this.dataSourcesVm = new PreviewInputGroupViewModel(
      getStorage,
      getLegendId,
      'previewer.datasourceinputs',
      createQuestionListViewModel,
    )
    this.organizerVm = new PreviewInputGroupViewModel(
      getStorage,
      getLegendId,
      'previewer.organizerCollectedValues',
      createQuestionListViewModel,
    )
    this.skillsVm = new SkillInputsViewModel(
      () => _options.catalog.skills,
      getStorage,
      getLegendId,
    )

    this._lifecycle.manage(this.questionsVm)
    this._lifecycle.manage(this.dataSourcesVm)
    this._lifecycle.manage(this.organizerVm)
    this._lifecycle.manage(this.skillsVm)
    this._lifecycle.manage(this._organizerQuestions$)
    this._lifecycle.manage(this._computations$)
  }

  /**
   * The latest computation. One with service items the legend can't use is an
   * error, because the job can't be priced.
   */
  @computed
  get result(): PreviewResult {
    const result = this._result
    if (result.type !== 'Success') {
      return result
    }
    const unknownIds = findUnknownInventoryItemIds(
      result.computation.inventoryItems,
      this._options.catalog.allowedInventoryItems,
    )
    return unknownIds.length === 0
      ? result
      : {
          type: 'Error',
          message: `The legend uses service items that aren't available: ${unknownIds.join(', ')}`,
          hasLegendErrors: false,
        }
  }

  /**
   * The latest successful computation.
   */
  @computed
  get computation() {
    return this.result.type === 'Success' ? this.result.computation : null
  }

  /**
   * Questions for the values the personal organizer collects, keyed by the
   * binding ID: the organizer sends one value per binding, and binding IDs
   * don't change when an element ID does.
   */
  @computed
  private get _collectedValueQuestions(): Question[] {
    return this._collectedValueElements.map(({ binding, element }) =>
      mapCollectedValueBindingToQuestion(binding, element.id),
    )
  }

  /**
   * Questions for the values of the routing context data sources the
   * computation asks for.
   */
  @computed
  private get _routingContextQuestions(): Question[] {
    return (this.computation?.routingContextDataSources ?? []).flatMap(
      (source, index) => {
        if (source.type === 'Unknown') {
          // An unknown source has no ID, but its row needs one. Source IDs
          // are alphanumeric, so `#` can't clash with a known source's.
          return [
            mapRoutingContextSourceToQuestion(source, `#unknown-${index}`),
          ]
        }
        return source.id
          ? [mapRoutingContextSourceToQuestion(source, source.id)]
          : []
      },
    )
  }

  @computed
  get hasRoutingContextSources() {
    return this._routingContextQuestions.length > 0
  }

  @computed
  get hasCollectedValues() {
    return this._collectedValueQuestions.length > 0
  }

  /**
   * The job's service items and prices.
   */
  @computed
  get pricing() {
    const computation = this.computation
    if (!computation) {
      return null
    }
    return computePricing({
      computedItems: computation.inventoryItems,
      inventoryItems: this._options.catalog.allowedInventoryItems,
      bundles: this._options.catalog.bundles,
      prepAndReviewConfig: this._prepAndReviewConfig,
    })
  }

  /**
   * Both the review and the prep-and-review items are on the job, which the
   * prep and review flow can't price.
   */
  @computed
  get hasConflictingReviewItems() {
    const config = this._prepAndReviewConfig
    const items = this.computation?.inventoryItems ?? []
    return (
      config.enabled &&
      items.some((item) => item.id === config.reviewServiceItemId) &&
      items.some((item) => item.id === config.prepAndReviewServiceItemId)
    )
  }

  @computed
  get variables(): PreviewVariable[] {
    return (this.computation?.variables ?? []).map((variable) => {
      const type = variable.valueType
      const isList = Types.isListType(type)
      return {
        id: variable.id,
        title: variable.label || variable.id,
        caption: variable.label ? variable.id : null,
        isList,
        values: isList
          ? (Array.isArray(variable.value) ? variable.value : []).map(
              (item: unknown) => formatPreviewValue(type.elementType, item),
            )
          : [formatPreviewValue(type, variable.value)],
      }
    })
  }

  @computed
  get organizerPrefill(): PreviewPrefill[] {
    const bindings = this._options.getOrganizerBindings()?.personal.prefill
    return (this.computation?.organizerPrefill ?? []).map((entry) => ({
      bindingId: entry.bindingId,
      title:
        bindings?.find((b) => b.id === entry.bindingId)?.displayName ??
        entry.bindingId,
      value: formatPrefillValue(entry.value),
    }))
  }

  @computed
  private get _collectedValueElements() {
    return this._options
      .getQuestions()
      .flatMap(
        (
          question,
        ): { element: typeof question; binding: OrganizerBinding }[] => {
          if (question.type !== 'PersonalOrganizerCollectedValue') {
            return []
          }
          const { binding } = question
          return binding ? [{ element: question, binding }] : []
        },
      )
  }

  /**
   * Answers for bindings no longer in the legend are left out.
   */
  @computed
  private get _collectedValues() {
    const bindings = this._collectedValueElements.map((e) => e.binding)
    return this.organizerVm.responses.flatMap((response) => {
      const binding = bindings.find((b) => b.id === response.id)
      return binding
        ? [
            {
              bindingId: binding.id,
              value: parseCollectedValue(response.answer, binding.valueType),
            },
          ]
        : []
    })
  }

  /**
   * Compared structurally, so editing the legend only recomputes when the
   * input changes.
   */
  @computed.struct
  private get _input(): Omit<LegendComputationInput, 'dateSubmitted'> {
    return {
      intakeResponses: this.questionsVm.responses,
      collectedValues: this._collectedValues,
      skills: this.skillsVm.selectedSkills,
      dataSourceInputs: this.dataSourcesVm.responses.map((response) => ({
        id: response.id,
        value: response.answer,
      })),
    }
  }

  private get _prepAndReviewConfig() {
    return this._options.getPrepAndReviewConfig()
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows what the engine computed for the job.
   */
  @action.bound
  showComputations() {
    this.outputTab = 'Computations'
  }

  /**
   * Shows the values of the legend's variables.
   */
  @action.bound
  showVariables() {
    this.outputTab = 'Variables'
  }

  /**
   * Goes back to the editor, to fix the legend's errors.
   */
  @action.bound
  goToEditor() {
    this._options.showEditor()
  }

  /**
   * Never rejects: a failed computation is shown in place of the outputs.
   *
   * @param legendVersion
   * @param input
   * @param signal
   */
  private async _compute(
    legendVersion: LegendVersion | null,
    input: Omit<LegendComputationInput, 'dateSubmitted'>,
    signal: AbortSignal,
  ): Promise<PreviewResult> {
    if (!legendVersion) {
      return { type: 'Loading' }
    }
    try {
      const computation = await this._options.legendStore.computeLegendVersion(
        {
          legendId: legendVersion.id,
          version: legendVersion.version,
          input: { ...input, dateSubmitted: new Date() },
        },
        signal,
      )
      return { type: 'Success', computation }
    } catch (error) {
      return {
        type: 'Error',
        message:
          (error instanceof Error && error.message) ||
          extractMessageFromError(error),
        hasLegendErrors:
          error instanceof LegendComputeError && error.errors.length > 0,
      }
    }
  }

  @action.bound
  private _receiveResult(result: PreviewResult) {
    this._result = result
    if (result.type === 'Success') {
      this.questionsVm.sync(result.computation.questions)
      this.dataSourcesVm.sync(this._routingContextQuestions)
    }
  }

  @action.bound
  private _setComputing(isComputing: boolean) {
    this.isComputing = isComputing
  }
}

/**
 * Numbers aren't locale-formatted, because values like tax years must show
 * as they are.
 *
 * @param value
 */
function formatPrefillValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.length === 0 ? '(empty list)' : value.join(', ')
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  return String(value)
}
